<section class="asb">

  <!-- ── Controls ── -->
  <div class="asb-controls">
    <div class="asb-search">
      <mat-icon>search</mat-icon>
      <input type="text"
             [(ngModel)]="searchText"
             (ngModelChange)="onVisibleSetChanged()"
             placeholder="Search any sensor detail…"
             aria-label="Search sensors" />
      <button *ngIf="searchText" class="asb-search-clear" (click)="searchText = ''" aria-label="Clear search">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div class="asb-right-controls">
      <!-- Sort (all users) -->
      <button type="button" class="asb-sort-btn" [matMenuTriggerFor]="sortMenu" aria-label="Sort">
        <mat-icon>swap_vert</mat-icon>
        <span class="asb-sort-label">{{ sortLabel() }}</span>
        <mat-icon class="asb-sort-caret">arrow_drop_down</mat-icon>
      </button>
      <mat-menu #sortMenu="matMenu">
        <ng-container *ngFor="let o of sortOptions">
          <button mat-menu-item *ngIf="!o.staffOnly || isStaff" (click)="setSort(o.key)">
            <mat-icon *ngIf="sortKey === o.key">check</mat-icon>
            <span>{{ o.label }}</span>
          </button>
        </ng-container>
      </mat-menu>

      <!-- Staff-only knobs -->
      <ng-container *ngIf="isStaff">
        <div class="asb-segmented" role="group" aria-label="Active filter">
          <button [class.active]="staffFilter === 'all'"      (click)="setStaffFilter('all')">All</button>
          <button [class.active]="staffFilter === 'active'"   (click)="setStaffFilter('active')">Active</button>
          <button [class.active]="staffFilter === 'inactive'" (click)="setStaffFilter('inactive')">Inactive</button>
        </div>

        <select class="asb-company" [(ngModel)]="companyFilter"
                (ngModelChange)="onVisibleSetChanged()" aria-label="Company">
          <option value="">All companies</option>
          <option *ngFor="let c of companies()" [value]="c">{{ c }}</option>
        </select>
      </ng-container>
    </div>
  </div>

  <!-- ── States ── -->
  <div class="asb-state" *ngIf="isLoading">
    <mat-icon class="spin">autorenew</mat-icon>
    <span>Loading sensors…</span>
  </div>

  <div class="asb-state asb-state--error" *ngIf="!isLoading && isError">
    <mat-icon>error_outline</mat-icon>
    <span>Couldn't load sensors. Please try again.</span>
  </div>

  <div class="asb-state" *ngIf="!isLoading && !isError && filteredSensors().length === 0">
    <mat-icon>sensors_off</mat-icon>
    <span>No sensors match.</span>
  </div>

  <!-- ── List ── -->
  <div class="asb-list" *ngIf="!isLoading && !isError">
    <!-- A real anchor, not a click handler: the row carries an href, so every way of
         opening a link works — plain click, right-click, middle-click, Cmd/Ctrl-click.
         target="_blank" makes the ordinary left-click open a new tab too, so the board
         stays put and several sensors can be opened side by side. -->
    <a class="asb-row"
       *ngFor="let s of filteredSensors(); trackBy: trackBySensor"
       [attr.data-status]="s.statusCategory"
       [routerLink]="sensorLink(s)"
       target="_blank"
       rel="noopener"
       [attr.aria-label]="'Open ' + s.name + ' live page in a new tab'">

      <!-- Status badge: KPI-card ring, spans both lines, centered -->
      <span class="asb-status-badge" [attr.data-status]="s.statusCategory"
            [matTooltip]="statusLabels[s.statusCategory]">
        <mat-icon>{{ statusIcons[s.statusCategory] }}</mat-icon>
      </span>

      <!-- Line 1: name + company + last contact + cycle -->
      <div class="asb-ident">
        <span class="asb-name">{{ s.name }}</span>
        <span class="asb-sub" *ngIf="isStaff && s.customerName">{{ s.customerName }}</span>
        <span class="asb-sub asb-inactive-tag" *ngIf="isStaff && !s.isActive">Inactive</span>
        <!-- Latest run carrying analytical data: when it ran, how long ago, and the
             freshness colour. Both parts sit on the row, not only in the tooltip. -->
        <span class="asb-ping" [attr.data-fresh]="s.freshnessTier" [matTooltip]="pingTooltip(s)">
          <mat-icon>science</mat-icon>
          <ng-container *ngIf="s.lastReportLocal; else noAnalytical">
            <span class="asb-ping-time">{{ s.lastReportLocal }}</span>
            <span class="asb-ping-ago" *ngIf="s.timeElapsed">· {{ s.timeElapsed }} ago</span>
          </ng-container>
          <ng-template #noAnalytical>
            <span class="muted">{{ ambientReady ? 'no analytical data' : '…' }}</span>
          </ng-template>
        </span>
      </div>

      <!-- Line 1 right: run state (+ cycle while running) and wind -->
      <div class="asb-detect">
        <span class="asb-runstate" [attr.data-status]="s.statusCategory">
          {{ statusLabels[s.statusCategory] }}
          <ng-container *ngIf="s.statusCategory === 'running' && s.cycle && s.cycle.total > 0">
            · Cycle {{ s.cycle.current }}/{{ s.cycle.total }}<ng-container
              *ngIf="s.cycle.percent != null"> · {{ s.cycle.percent }}%</ng-container>
          </ng-container>
        </span>
        <span class="asb-fw" *ngIf="s.interior?.firmware" [matTooltip]="firmwareTooltip(s)">
          <mat-icon>memory</mat-icon>{{ s.interior.firmware }}
        </span>
        <span class="asb-wind" *ngIf="hasWind(s)" [matTooltip]="windTooltip(s)">
          <mat-icon>air</mat-icon>{{ windText(s) }}
        </span>
      </div>

      <!-- Line 2 left: live interior telemetry (under the sensor) -->
      <div class="asb-interior">
        <span class="asb-group-tag asb-group-tag--live" matTooltip="Sensor interior — live from device">Interior</span>
        <span class="asb-metric" matTooltip="Interior temperature (°C)">
          <mat-icon>thermostat</mat-icon>{{ s.interior && s.interior.temp != null ? (s.interior.temp | number:'1.0-1') + '°' : 'N/A' }}
        </span>
        <span class="asb-metric" matTooltip="Interior humidity (%)">
          <mat-icon>water_drop</mat-icon>{{ s.interior && s.interior.humidity != null ? (s.interior.humidity | number:'1.0-0') + '%' : 'N/A' }}
        </span>
      </div>

      <!-- Line 2 right: outdoor ambient, under detected chemical (SQL snapshot) -->
      <div class="asb-ambient">
        <span class="asb-group-tag" matTooltip="Outdoor ambient — refreshed on run/cycle change">Ambient</span>
        <span class="asb-metric" matTooltip="Ambient temperature (°C)">
          <mat-icon>thermostat</mat-icon>{{ s.vitals.ambientTemp != null ? (s.vitals.ambientTemp | number:'1.0-1') + '°' : (ambientReady ? 'N/A' : '…') }}
        </span>
        <span class="asb-metric" matTooltip="Ambient humidity (%)">
          <mat-icon>water_drop</mat-icon>{{ s.vitals.ambientHumidity != null ? (s.vitals.ambientHumidity | number:'1.0-0') + '%' : (ambientReady ? 'N/A' : '…') }}
        </span>
        <span class="asb-metric" matTooltip="Ambient pressure (hPa)">
          <mat-icon>compress</mat-icon>{{ s.vitals.ambientPressure != null ? (s.vitals.ambientPressure | number:'1.0-0') : (ambientReady ? 'N/A' : '…') }}
        </span>
      </div>
    </a>
  </div>

</section>
